// The phone board's pager: a scroll-snap scroller with one column per
// scroller width, and the tab strip above it. The two stay in sync from both
// sides: a tap on a tab scrolls the scroller, a swipe moves the highlight.
//
// A tap scrolls smoothly past the columns in between, and the scroll events on
// the way would move the highlight through every one of them. So a tap sets a
// target, and scroll events are ignored until the scroller arrives there (or a
// finger lands on it, or the browser reports the scroll ended, or a timeout
// passes, for a browser that reports nothing and a target that is never
// reached).
//
// The reader's column is kept by id, not by position: the columns can be
// reordered or lose one while the board is open, and a position would then
// point at another column (and the remembered id at none).

import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import { indexFromScroll, initialIndex } from './columnPager';
import { readLastColumn, writeLastColumn } from './lastColumn';

// Longer than a smooth scroll across the widest board takes. The fallback for
// a browser without `scrollend`; where it exists the target is released the
// moment the scroll ends.
const TARGET_TIMEOUT_MS = 800;

function browserStorage(): Storage | undefined {
    try {
        return typeof localStorage === 'undefined' ? undefined : localStorage;
    } catch {
        // Merely reading the property throws when site data is blocked.
        return undefined;
    }
}

function prefersReducedMotion(): boolean {
    return (
        typeof window !== 'undefined' &&
        Boolean(
            window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches
        )
    );
}

export function useColumnPager(
    ids: readonly string[],
    storage: Storage | undefined = browserStorage()
) {
    const scrollerRef = useRef<HTMLDivElement | null>(null);
    const [activeId, setActiveId] = useState<string | undefined>(
        () => ids[initialIndex(ids, readLastColumn(storage))]
    );
    // What the scroll handler compares against, without a re-render per event.
    const activeRef = useRef(activeId);
    // Where the reader's column sat in the row the last time we looked: when it
    // goes, the reader stays at that place and gets whichever column is there.
    const positionRef = useRef(ids.indexOf(activeId ?? ''));
    const target = useRef<number | null>(null);
    const targetTimer = useRef<number | null>(null);
    const idsRef = useRef(ids);
    idsRef.current = ids;
    const storageRef = useRef(storage);
    storageRef.current = storage;

    const clearTarget = useCallback(() => {
        target.current = null;
        if (targetTimer.current !== null) {
            window.clearTimeout(targetTimer.current);
            targetTimer.current = null;
        }
    }, []);

    // The index of the reader's column in the current row, or, when it is
    // gone, the place it had, clamped into the row.
    const currentIndex = useCallback(() => {
        const list = idsRef.current;
        const at =
            activeRef.current === undefined
                ? -1
                : list.indexOf(activeRef.current);
        if (at >= 0) return at;
        return Math.max(Math.min(positionRef.current, list.length - 1), 0);
    }, []);

    // Make `id` the reader's column and remember it.
    const adopt = useCallback((id: string | undefined, position: number) => {
        positionRef.current = Math.max(position, 0);
        if (id === undefined || activeRef.current === id) return;
        activeRef.current = id;
        setActiveId(id);
        writeLastColumn(storageRef.current, id);
    }, []);

    const commit = useCallback(
        (next: number) => adopt(idsRef.current[next], next),
        [adopt]
    );

    // Before the first paint, so the board never shows column one and jumps;
    // and again whenever the columns change, so the scroller keeps showing the
    // column the reader is on (or the one that took its place).
    const idsKey = ids.join('\u0000');
    useLayoutEffect(() => {
        const el = scrollerRef.current;
        const at = currentIndex();
        adopt(idsRef.current[at], at);
        if (!el) return;
        clearTarget();
        el.scrollLeft = at * el.clientWidth;
    }, [idsKey, currentIndex, adopt, clearTarget]);

    useEffect(() => clearTarget, [clearTarget]);

    const select = useCallback(
        (requested: number) => {
            const last = Math.max(idsRef.current.length - 1, 0);
            const next = Math.min(Math.max(requested, 0), last);
            commit(next);
            const el = scrollerRef.current;
            if (!el) return;
            const left = next * el.clientWidth;
            clearTarget();
            if (Math.abs(el.scrollLeft - left) < 1) return;
            target.current = next;
            targetTimer.current = window.setTimeout(
                clearTarget,
                TARGET_TIMEOUT_MS
            );
            if (typeof el.scrollTo === 'function') {
                el.scrollTo({
                    left,
                    behavior: prefersReducedMotion() ? 'auto' : 'smooth',
                });
            } else {
                el.scrollLeft = left;
            }
        },
        [commit, clearTarget]
    );

    // The column the scroller is closest to becomes the reader's.
    const follow = useCallback(() => {
        const el = scrollerRef.current;
        if (!el) return;
        commit(
            indexFromScroll(
                el.scrollLeft,
                el.clientWidth,
                idsRef.current.length
            )
        );
    }, [commit]);

    const onScroll = useCallback(() => {
        const el = scrollerRef.current;
        if (!el) return;
        if (target.current !== null) {
            const at = indexFromScroll(
                el.scrollLeft,
                el.clientWidth,
                idsRef.current.length
            );
            if (at === target.current) clearTarget();
            return;
        }
        follow();
    }, [follow, clearTarget]);

    // The browser says when a scroll has ended (Chromium 114+, Firefox 109+,
    // Safari 26+): that is the moment to stop waiting for a tapped tab's
    // column, whether the scroll arrived or something took it over. Followed
    // where it stopped, so the highlight cannot be left on a column the
    // scroller is not at. A listener, not a prop: React 18 has no onScrollEnd.
    useEffect(() => {
        const el = scrollerRef.current;
        if (!el) return;
        const onScrollEnd = () => {
            if (target.current === null) return;
            clearTarget();
            follow();
        };
        el.addEventListener('scrollend', onScrollEnd);
        return () => el.removeEventListener('scrollend', onScrollEnd);
    }, [follow, clearTarget]);

    // A new width (rotation, a window dragged across 600 px) would leave the
    // scroller between two columns; stay on the one the reader is on.
    const realign = useCallback(() => {
        const el = scrollerRef.current;
        if (el) el.scrollLeft = currentIndex() * el.clientWidth;
    }, [currentIndex]);

    useEffect(() => {
        const el = scrollerRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return;
        let width = el.clientWidth;
        const observer = new ResizeObserver(() => {
            if (el.clientWidth === width) return;
            width = el.clientWidth;
            realign();
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, [realign]);

    // By id; a column that is gone leaves the reader at the same place in the
    // row, until the layout effect above has adopted the column there.
    const last = Math.max(ids.length - 1, 0);
    const at = activeId === undefined ? -1 : ids.indexOf(activeId);
    const active = at >= 0 ? at : Math.min(positionRef.current, last);

    return {
        active: Math.max(active, 0),
        select,
        realign,
        scrollerProps: {
            ref: scrollerRef,
            onScroll,
            // A finger on the scroller ends the wait for a tapped tab's column.
            onTouchStart: clearTarget,
        },
    };
}
